import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "@/layouts/MainLayout.tsx";
import useAuth from "@/hooks/useAuth.ts";
import { useEffect, useState } from "react";
import {
  Loader2, Users, AlertTriangle, UserPlus, Mic, Calendar,
  FileText, Upload, Bell,
  Download, Brain, UserX, Phone, Check, Building2,
  Clock, Search, LayoutDashboard
} from "lucide-react";
import AuthService from "@/lib/auth.ts";
import MetricCard from "@/components/custom/MetricCard.tsx";
import ActivityCard from "@/components/custom/ActivityCard.tsx";
import type { ActivityItem as SharedActivityItem } from "@/components/custom/ActivityCard.tsx";
import MindWiseDashboardBanner from "@/components/mindwise/MindWiseDashboardBanner.tsx";
import MindWiseRosterModal from "@/components/mindwise/MindWiseRosterModal.tsx";

// Type definitions
export interface UnrespondedPatient {
  encounter_id: string;
  patient_id: string;
  first_name: string;
  last_name: string;
  mobile_number: string;
  email: string;
  start_time: string;
  encounter_date: string;
  encounter_type: string;
  status: string;
  provider_name: string;
  hospital_name: string;
  branch_name: string;
  full_address: string;
  clinic_phone: string;
  reminder_cadence_stage: string;
  patient_response_status: string;
  last_reminder_sent_at?: string;
  staff_followup_notified_at?: string;
}

// Type definitions
interface DashboardData {
  totalPatients: number | null;
  pendingQuestionnaires: number | null;
  completedQuestionnaires: number | null;
  newRegistrationsToday: number | null;
  totalEncounters: number | null;
  audioRecordings: number | null;
  staffMembers: number | null;
  activeCritical: number | null;
  activeWarning: number | null;
  unrespondedCount: number | null;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, isLoading } = useAuth();

  const [dashboardData, setDashboardData] = useState<DashboardData>({
    totalPatients: null,
    pendingQuestionnaires: null,
    completedQuestionnaires: null,
    newRegistrationsToday: null,
    totalEncounters: null,
    audioRecordings: null,
    staffMembers: null,
    activeCritical: null,
    activeWarning: null,
    unrespondedCount: null,
  });

  const [recentActivities, setRecentActivities] = useState<SharedActivityItem[]>([]);
  const [pendingReviews, setPendingReviews] = useState<SharedActivityItem[]>([]);
  const [questionnaireStats, setQuestionnaireStats] = useState<SharedActivityItem[]>([]);
  const [unrespondedList, setUnrespondedList] = useState<UnrespondedPatient[]>([]);
  const [unrespondedSearch, setUnrespondedSearch] = useState("");
  const [activeTab, setActiveTab] = useState<"overview" | "unresponded">("overview");
  const [contactingId, setContactingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMindWiseRosterOpen, setIsMindWiseRosterOpen] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      if (!token) {
        navigate("/login", {
          state: { from: location.pathname || "/" },
          replace: true,
        });
      } else {
        fetchEnhancedDashboardData();
      }
    }
  }, [token, isLoading, navigate, location.pathname]);

  const fetchEnhancedDashboardData = async () => {
    try {
      setLoading(true);
      const [data, cmsStats, unrespondedData] = await Promise.all([
        AuthService.getDashboardSummary(),
        AuthService.getCmsAlertStats({ days: 30 }),
        AuthService.getUnrespondedPatients(),
      ]);

      const unresponded: UnrespondedPatient[] = Array.isArray(unrespondedData) ? unrespondedData : [];
      setUnrespondedList(unresponded);

      if (data) {
        // ── Map real API shape ──────────────────────────────────────────────
        // Response: { patients, encounters, questionnaires, staff, recentActivity[] }

        const activity: any[] = data.recentActivity || [];

        setDashboardData({
          totalPatients: data.patients?.total ?? null,
          pendingQuestionnaires: data.questionnaires?.pending ?? null,
          completedQuestionnaires: data.questionnaires?.reviewed ?? null,
          newRegistrationsToday: null,   // not in current API — request from backend
          totalEncounters: data.encounters?.total ?? null,
          audioRecordings: null,   // not in current API — request from backend
          staffMembers: data.staff?.total ?? null,
          activeCritical: cmsStats?.active_critical ?? null,
          activeWarning: cmsStats?.active_warning ?? null,
          unrespondedCount: data.encounters?.unresponded ?? unresponded.length,
        });

        // ── Recent Activity feed ────────────────────────────────────────────
        // Format each recentActivity item as { label, value }
        const formattedActivity: SharedActivityItem[] = activity.map((a) => {
          const date = new Date(a.created_at);
          const dateStr = isNaN(date.getTime())
            ? a.created_at
            : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
          return {
            label: `${a.questionnaire_type} — ${a.patient_name}`,
            value: `Score: ${a.total_score}  ·  ${dateStr}`,
          };
        });
        setRecentActivities(formattedActivity);

        // ── Pending Reviews (items not yet reviewed) ────────────────────────
        const pending = activity.filter((a) =>
          a.status?.toLowerCase().includes("pending") ||
          a.status?.toLowerCase().includes("submitted")
        );

        const formattedReviews: SharedActivityItem[] = pending.map((a) => {
          const getSeverity = (type: string, score: number) => {
            if (type === "PHQ-9") {
              if (score >= 20) return { urgency: "Severe", urgencyColor: "bg-red-50 text-red-600" };
              if (score >= 15) return { urgency: "High", urgencyColor: "bg-orange-50 text-orange-600" };
              if (score >= 10) return { urgency: "Medium", urgencyColor: "bg-amber-50 text-amber-600" };
            } else if (type === "GAD-7") {
              if (score >= 15) return { urgency: "Severe", urgencyColor: "bg-red-50 text-red-600" };
              if (score >= 10) return { urgency: "High", urgencyColor: "bg-orange-50 text-orange-600" };
              if (score >= 5) return { urgency: "Medium", urgencyColor: "bg-amber-50 text-amber-600" };
            }
            return { urgency: "Low", urgencyColor: "bg-emerald-50 text-emerald-700" };
          };

          const { urgency, urgencyColor } = getSeverity(a.questionnaire_type, a.total_score);
          return {
            label: `${a.questionnaire_type} — ${a.patient_name}`,
            value: `Score: ${a.total_score}${a.highest_alert_severity ? " · CMS alert fired" : ""}`,
            status: urgency,
            statusColor: urgencyColor,
          };
        });

        setPendingReviews(formattedReviews);

        // ── Questionnaire stats summary ────────────────────────────────────
        setQuestionnaireStats([
          { label: "Total Questionnaires", value: String(data.questionnaires?.total ?? "—") },
          { label: "Pending Review", value: String(data.questionnaires?.pending ?? "—") },
          { label: "Reviewed", value: String(data.questionnaires?.reviewed ?? "—") },
          { label: "Active CMS Alerts", value: String((cmsStats?.active_critical ?? 0) + (cmsStats?.active_warning ?? 0)) },
          { label: "Encounters Today", value: String(data.encounters?.today ?? "—") },
          { label: "Scheduled", value: String(data.encounters?.scheduled ?? "—") },
          { label: "Completed", value: String(data.encounters?.completed ?? "—") },
        ]);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers for new buttons ---
  const handleImportAppointments = () => {
    setIsMindWiseRosterOpen(true);
  };

  const handleExportScores = () => {
    // TODO: wire up export logic
    console.log("Export Patients Score");
  };

  const handleMarkContacted = async (encounterId: string) => {
    try {
      setContactingId(encounterId);
      await AuthService.markPatientContacted(encounterId);
      setUnrespondedList((prev) =>
        prev.map((item) =>
          item.encounter_id === encounterId
            ? { ...item, patient_response_status: "staff_contacted" }
            : item
        )
      );
    } catch (err) {
      console.error("Error marking patient contacted:", err);
    } finally {
      setContactingId(null);
    }
  };

  const filteredUnresponded = unrespondedList.filter((item) => {
    if (!unrespondedSearch.trim()) return true;
    const q = unrespondedSearch.toLowerCase();
    const name = `${item.first_name} ${item.last_name}`.toLowerCase();
    const phone = (item.mobile_number || "").toLowerCase();
    const prov = (item.provider_name || "").toLowerCase();
    const hosp = (item.hospital_name || "").toLowerCase();
    return name.includes(q) || phone.includes(q) || prov.includes(q) || hosp.includes(q);
  });

  if (isLoading || loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a7a4a]" />
        </div>
      </MainLayout>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] p-6 font-sans">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* ── Header ── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                Healthcare Dashboard
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Real-time overview of your mental health management system
              </p>
            </div>

            {/* ── Action Buttons ── */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* MindWise Weekly Ingestion Badge Button */}
              <button
                onClick={() => setIsMindWiseRosterOpen(true)}
                className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50/90 via-indigo-50/70 to-emerald-50/90 text-blue-900 text-sm font-semibold hover:border-blue-400 hover:shadow-xs transition-all duration-150 shadow-2xs"
                title="Click to view this week's MindWise EHR ingested roster"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <Brain className="h-4 w-4 text-blue-700" />
                <span>MindWise Ingested</span>
                <span className="bg-blue-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full shadow-2xs">
                  This Week (5)
                </span>
              </button>

              {/* Import Patients Appointments */}
              <button
                onClick={handleImportAppointments}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#1a7a4a] text-[#1a7a4a] text-sm font-semibold bg-white hover:bg-[#e8f5ee] transition-colors duration-150 shadow-sm"
              >
                <Download className="h-4 w-4" />
                Import Appointments
              </button>

              {/* Export Patients Score */}
              <button
                onClick={handleExportScores}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1a7a4a] text-white text-sm font-semibold hover:bg-[#155e39] transition-colors duration-150 shadow-md shadow-[#1a7a4a]/20"
              >
                <Upload className="h-4 w-4" />
                Export Scores
              </button>
            </div>
          </div>

          {/* ── MindWise Weekly Ingestion Banner ── */}
          <MindWiseDashboardBanner onOpenRosterModal={() => setIsMindWiseRosterOpen(true)} />

          {/* ── Primary Metrics ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Patients"
              value={dashboardData.totalPatients}
              icon={Users}
              subtitle="Registered in system"
              accent={true}
              onClick={() => navigate("/patients/search")}
            />
            <MetricCard
              title="Pending Reviews"
              value={dashboardData.pendingQuestionnaires}
              icon={AlertTriangle}
              subtitle="Awaiting review"
              trendColor="text-orange-500"
              onClick={() => navigate("/questionnaire-responses")}
            />
            <MetricCard
              title="CMS Alerts"
              value={(dashboardData.activeCritical ?? 0) + (dashboardData.activeWarning ?? 0)}
              icon={Bell}
              subtitle={`${dashboardData.activeCritical ?? 0} critical · ${dashboardData.activeWarning ?? 0} warning`}
              trendColor="text-red-500"
              onClick={() => navigate("/cms-alerts")}
            />
            <MetricCard
              title="New Today"
              value={dashboardData.newRegistrationsToday}
              icon={UserPlus}
              subtitle="Registrations today"
              trendColor="text-emerald-600"
              trend="↑"
            />
          </div>

          {/* ── Secondary Metrics ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard
              title="Completed Reviews"
              value={dashboardData.completedQuestionnaires}
              icon={FileText}
              subtitle="Successfully reviewed"
              trendColor="text-emerald-600"
            />
            <MetricCard
              title="Unresponded Patients"
              value={dashboardData.unrespondedCount}
              icon={UserX}
              subtitle="Awaiting appointment response"
              trendColor="text-amber-600"
              accent={(dashboardData.unrespondedCount ?? 0) > 0}
              onClick={() => setActiveTab("unresponded")}
            />
            <MetricCard
              title="Audio Recordings"
              value={dashboardData.audioRecordings}
              icon={Mic}
              subtitle="Voice responses"
              onClick={() => navigate("/admin/audio-archives")}
            />
            <MetricCard
              title="Total Encounters"
              value={dashboardData.totalEncounters}
              icon={Calendar}
              subtitle="Patient encounters"
              onClick={() => navigate("/appointments")}
            />
            <MetricCard
              title="Staff Members"
              value={dashboardData.staffMembers}
              icon={Users}
              subtitle="Active healthcare staff"
              onClick={() => navigate("/admin/staff")}
            />
          </div>

          {/* ── Dashboard Tabs: System Overview vs Unresponded Patients ── */}
          <div className="flex items-center gap-4 border-b border-gray-200 pt-2">
            <button
              onClick={() => setActiveTab("overview")}
              className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-all duration-150 flex items-center gap-2 ${
                activeTab === "overview"
                  ? "border-[#1a7a4a] text-[#1a7a4a]"
                  : "border-transparent text-gray-400 hover:text-gray-700"
              }`}
            >
              <LayoutDashboard className="h-4 w-4" />
              <span>System Overview</span>
            </button>

            <button
              onClick={() => setActiveTab("unresponded")}
              className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-all duration-150 flex items-center gap-2 relative ${
                activeTab === "unresponded"
                  ? "border-amber-600 text-amber-800"
                  : "border-transparent text-gray-400 hover:text-gray-700"
              }`}
            >
              <UserX className="h-4 w-4 text-amber-600" />
              <span>Unresponded Patients</span>
              {(dashboardData.unrespondedCount ?? 0) > 0 && (
                <span className="bg-amber-100 text-amber-800 text-[11px] font-bold px-2 py-0.5 rounded-full border border-amber-200">
                  {dashboardData.unrespondedCount}
                </span>
              )}
            </button>
          </div>

          {/* ── Tab Content: System Overview ── */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              <ActivityCard
                title="Priority Reviews"
                items={pendingReviews}
                emptyMessage="No pending reviews"
              />
              <ActivityCard
                title="Recent Activity"
                items={recentActivities}
                emptyMessage="No recent activity"
              />
              <ActivityCard
                title="Assessment Overview"
                items={questionnaireStats}
                emptyMessage="No assessments completed"
              />
            </div>
          )}

          {/* ── Tab Content: Unresponded Patients Card ── */}
          {activeTab === "unresponded" && (
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
              {/* Card Header */}
              <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <UserX className="h-5 w-5 text-amber-600" />
                    <h2 className="text-base font-bold text-gray-900 tracking-tight">
                      Unresponded Patient Encounters
                    </h2>
                    <span className="bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-200">
                      {filteredUnresponded.length} pending
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Patients who have not yet confirmed their appointment reminders or flagged unresponsive for staff follow-up
                  </p>
                </div>

                {/* Search Bar */}
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={unrespondedSearch}
                    onChange={(e) => setUnrespondedSearch(e.target.value)}
                    placeholder="Search by name, phone..."
                    className="w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#1a7a4a] focus:border-[#1a7a4a]"
                  />
                </div>
              </div>

              {/* Patient List Table */}
              {filteredUnresponded.length === 0 ? (
                <div className="py-16 text-center text-gray-400 space-y-2">
                  <div className="w-12 h-12 rounded-full bg-emerald-50 text-[#1a7a4a] flex items-center justify-center mx-auto mb-2">
                    <Check className="h-6 w-6" />
                  </div>
                  <p className="text-sm font-semibold text-gray-700">All Patients Responded</p>
                  <p className="text-xs text-gray-400 max-w-sm mx-auto">
                    There are no unresponded patient appointment reminders requiring staff follow-up at this time.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-50/80 border-b border-gray-100 text-gray-400 font-semibold uppercase tracking-wider text-[10px]">
                        <th className="py-3 px-4">Patient</th>
                        <th className="py-3 px-4">Appointment Time</th>
                        <th className="py-3 px-4">Hospital & Branch Location</th>
                        <th className="py-3 px-4">Provider</th>
                        <th className="py-3 px-4">Cadence Stage</th>
                        <th className="py-3 px-4">Response Status</th>
                        <th className="py-3 px-4 text-right">Staff Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredUnresponded.map((item) => {
                        const dateObj = item.start_time ? new Date(item.start_time) : null;
                        const timeStr = dateObj && !isNaN(dateObj.getTime())
                          ? dateObj.toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            })
                          : item.encounter_date || "—";

                        const isContacted = item.patient_response_status === "staff_contacted";
                        const isUnresponsive = item.patient_response_status === "unresponsive";

                        return (
                          <tr key={item.encounter_id} className="hover:bg-amber-50/20 transition-colors duration-100">
                            {/* Patient Info */}
                            <td className="py-3.5 px-4">
                              <span className="font-semibold text-gray-900 block text-xs">
                                {item.first_name} {item.last_name}
                              </span>
                              {item.mobile_number ? (
                                <a
                                  href={`tel:${item.mobile_number}`}
                                  className="text-[#1a7a4a] hover:underline font-mono text-[11px] block mt-0.5"
                                >
                                  {item.mobile_number}
                                </a>
                              ) : (
                                <span className="text-gray-400 text-[11px] block mt-0.5">No phone</span>
                              )}
                            </td>

                            {/* Appointment Time */}
                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-1.5 font-medium text-gray-800">
                                <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                <span>{timeStr}</span>
                              </div>
                              <span className="text-[10px] text-gray-400 capitalize block mt-0.5">
                                {item.encounter_type || "Appointment"}
                              </span>
                            </td>

                            {/* Hospital & Location */}
                            <td className="py-3.5 px-4 max-w-xs">
                              <div className="flex items-start gap-1.5">
                                <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0 mt-0.5" />
                                <div>
                                  <span className="font-medium text-gray-900 block truncate">
                                    {item.hospital_name || "MonitorHealth"}
                                    {item.branch_name && ` (${item.branch_name})`}
                                  </span>
                                  {item.full_address && (
                                    <span className="text-[11px] text-gray-400 block truncate" title={item.full_address}>
                                      {item.full_address}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Provider */}
                            <td className="py-3.5 px-4 text-gray-700 font-medium">
                              {item.provider_name || "—"}
                            </td>

                            {/* Cadence Stage */}
                            <td className="py-3.5 px-4">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                {item.reminder_cadence_stage === "48h" && "48h Reminder Sent"}
                                {item.reminder_cadence_stage === "24h" && "24h Reminder Sent"}
                                {item.reminder_cadence_stage === "12h" && "12h Reminder Sent"}
                                {item.reminder_cadence_stage === "day_of" && "Day-of Reminder Sent"}
                                {item.reminder_cadence_stage === "12h_followup" && "12h Follow-up Sent"}
                                {item.reminder_cadence_stage === "unresponsive" && "Reminder Expired"}
                                {!["48h", "24h", "12h", "day_of", "12h_followup", "unresponsive"].includes(item.reminder_cadence_stage) &&
                                  (item.reminder_cadence_stage || "Pending Initial")}
                              </span>
                            </td>

                            {/* Response Status */}
                            <td className="py-3.5 px-4">
                              {isContacted ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <Check className="h-3 w-3" />
                                  Staff Contacted
                                </span>
                              ) : isUnresponsive ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200">
                                  <AlertTriangle className="h-3 w-3" />
                                  Unresponsive
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                  <Clock className="h-3 w-3" />
                                  Awaiting Response
                                </span>
                              )}
                            </td>

                            {/* Actions */}
                            <td className="py-3.5 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {item.mobile_number && (
                                  <a
                                    href={`tel:${item.mobile_number}`}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-[#1a7a4a] text-xs font-semibold border border-emerald-200 transition-colors duration-150"
                                    title={`Call ${item.first_name}`}
                                  >
                                    <Phone className="h-3 w-3" />
                                    <span>Call</span>
                                  </a>
                                )}

                                {!isContacted && (
                                  <button
                                    disabled={contactingId === item.encounter_id}
                                    onClick={() => handleMarkContacted(item.encounter_id)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white hover:bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200 transition-colors duration-150 disabled:opacity-50"
                                    title="Mark that staff has contacted the patient"
                                  >
                                    {contactingId === item.encounter_id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Check className="h-3 w-3 text-emerald-600" />
                                    )}
                                    <span>Followed Up</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── MindWise Weekly Ingested Roster Modal ── */}
          <MindWiseRosterModal
            open={isMindWiseRosterOpen}
            onOpenChange={setIsMindWiseRosterOpen}
          />

        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;